import {
  Component,
  OnInit,
  ElementRef,
  inject,
  input,
  output,
  ViewChild,
  HostBinding,
  HostListener
} from "@angular/core";
import { Observable } from "rxjs";
import { ConversationNotification } from "../../models/notifications/ConversationNotification";
import {
  FileTransferNotification,
  INotificationEvent,
  ConfirmNotification,
  ToastNotification
} from "../../models";

@Component({
  selector: "app-notification",
  templateUrl: "./notification.component.html",
  styleUrls: ["./notification.component.scss"],
  standalone: false
})
export class NotificationComponent implements OnInit {
  private host = inject<ElementRef<HTMLDivElement>>(ElementRef);

  readonly notification = input<INotificationEvent>(undefined);
  readonly notificationIndex = input<number>(0);
  readonly notificationsTotal = input<number>(1);

  readonly removed = output<INotificationEvent>();

  @ViewChild("cancelBtn", { read: ElementRef })
  cancelBtnRef: ElementRef<HTMLButtonElement>;

  @ViewChild("confirmBtn", { read: ElementRef })
  confirmBtnRef: ElementRef<HTMLButtonElement>;

  @HostBinding("attr.tabindex") tabindex = 0;

  @HostBinding("attr.aria-setsize") get ariaSetsize() {
    return this.notificationsTotal;
  }

  @HostBinding("attr.aria-posinset") get ariaPosinset() {
    return this.notificationIndex() + 1;
  }

  ngOnInit(): void {
    this.host.nativeElement.setAttribute(
      "role",
      this.isToast ? "alert" : "alertdialog"
    );
    this.host.nativeElement.setAttribute(
      "data-tid",
      "notification/" + this.notification().getType()
    );
    this.host.nativeElement.setAttribute("aria-label", this.buildAriaLabel());
  }

  @HostListener("keydown", ["$event"])
  onKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      if (!this.isDismissible) {
        return;
      }
      event.stopPropagation();
      if (this.notification() instanceof ConfirmNotification) {
        (this.notification() as ConfirmNotification).cancel();
      }
      this.remove();
    } else if (
      (event.key === "Enter" || event.key === " ") &&
      (this.isToast || this.isConversation)
    ) {
      event.preventDefault();
      this.clicked();
    }
  }

  get title() {
    return this.notification().getTitle();
  }

  get body() {
    return this.notification().getBody();
  }

  get color() {
    return this.notification().getColor();
  }

  get isFileTransfer() {
    return this.notification() instanceof FileTransferNotification;
  }

  get isConfirm() {
    return this.notification() instanceof ConfirmNotification;
  }

  get isDismissible() {
    return this.notification().isDismissible?.() ?? true;
  }

  get confirmLabel() {
    return (
      (this.notification() as ConfirmNotification).getConfirmLabel?.() || "Yes"
    );
  }

  get isConversation() {
    return this.notification() instanceof ConversationNotification;
  }

  get isToast() {
    return this.notification() instanceof ToastNotification;
  }

  get hostEl(): HTMLElement {
    return this.host.nativeElement;
  }

  focus() {
    if (this.isConfirm && this.cancelBtnRef) {
      this.cancelBtnRef.nativeElement.focus();
    } else if (this.isConfirm && this.confirmBtnRef) {
      this.confirmBtnRef.nativeElement.focus();
    } else {
      this.host.nativeElement.focus();
    }
  }

  private buildAriaLabel(): string {
    const position =
      this.notificationsTotal() > 1
        ? ` (${this.notificationIndex() + 1} of ${this.notificationsTotal()})`
        : "";
    return `Notification${position}: ${this.notification().getTitle()}`;
  }

  get url(): Observable<string> {
    return (this.notification() as FileTransferNotification).getFileUrl();
  }

  clicked() {
    if (this.isToast) {
      this.remove();
      return;
    }
    if (!this.isConversation) {
      return;
    }
    (this.notification() as ConversationNotification).click();
  }

  remove() {
    if (this.notification() instanceof ConversationNotification) {
      (this.notification() as ConversationNotification).dismiss?.();
    } else if (this.notification() instanceof ToastNotification) {
      (this.notification() as ToastNotification).dismiss?.();
    }
    this.removed.emit(this.notification());
  }

  approve() {
    (this.notification() as ConfirmNotification).confirm();
    this.remove();
  }

  deny() {
    (this.notification() as ConfirmNotification).cancel();
    this.remove();
  }
}
